import { useMutationState, useQuery } from '@tanstack/react-query';
import type { PosDataSource } from '../lib/posData/types';

/**
 * A small "syncing now" indicator - separate from `PosStatusChip`'s static
 * "N to send" count, which only ever reports the queue's resting state
 * between cycles. Two signals combine to decide whether a sync cycle is
 * genuinely running right this moment:
 *
 *  - the manual "Sync Now" mutation's own `isPending` state (the same
 *    mutation `PosStatusChip` already drives, now tagged with the
 *    `pos-sync-now` mutation key below so this component can watch it too)
 *    - zero latency, exact.
 *  - a 2s poll of `getSyncStatus()` for `'SYNCING'`, which also catches the
 *    engine's own 20s background timer (`posData/index.ts`'s
 *    `startBackgroundSync`). `PosStatusChip`'s existing poll for the same
 *    query is 10s, tuned for a passive "N to send" count where staleness
 *    barely matters; an active "syncing now" indicator is worth a tighter
 *    poll so it actually catches cycles that finish well inside 10s.
 *    Never a network call itself - `getSyncStatus()` reads local
 *    engine/DB state only (`SyncEngine.status()`).
 *
 * Renders nothing outside local mode (`source.mode === 'api'` has no sync
 * engine to report on - `getSyncStatus()` is always `null` there, per the
 * `PosDataSource` contract) and nothing while idle, matching
 * `PosStatusChip`'s own "say nothing when there is nothing to say"
 * convention.
 */
export function SyncActivityIndicator({ source }: { source: PosDataSource }) {
  const status = useQuery({
    queryKey: ['pos-sync-status'],
    queryFn: () => source.getSyncStatus(),
    refetchInterval: 2_000,
    enabled: source.mode === 'local',
  });

  const syncNowStates = useMutationState({
    filters: { mutationKey: ['pos-sync-now'] },
    select: (mutation) => mutation.state.status,
  });
  const manuallySyncing = syncNowStates.some((state) => state === 'pending');

  if (source.mode !== 'local') return null;
  if (!manuallySyncing && status.data !== 'SYNCING') return null;

  return (
    <span className="chip info" title="Sending queued bills to the server now.">
      <span className="spinner" style={{ width: 11, height: 11, borderWidth: 2 }} />
      Syncing…
    </span>
  );
}
